import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import { BASE_URL } from '@src/helpers/constants';
import { retrieveItem } from '@src/helpers/functions';
import * as TYPES from '@src/redux/types';

const GRAPHQL_QUERY = `
  query exportReadingList(
    $filter: readingFilterInput!
    $data: emailIdListInput!
  ) {
    exportReadingList(filter: $filter, data: $data) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.MutationResponseState = {
  result: null,
  error: null,
  loading: false
};

export const exportReadingListRequest = createAsyncThunk<
  TYPES.MutationResponse,
  {filter: TYPES.ReadingFilterInput;data: TYPES.EmailIdListInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'reading/export_reading_list',
  async (
  params: {filter: TYPES.ReadingFilterInput;data: TYPES.EmailIdListInput;}) =>
  {
    const sessionId = retrieveItem('sessionId') ?? '';
    try {
      const response = await fetch(BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          sessionId: sessionId
        },
        body: JSON.stringify({
          operationName: 'exportReadingList',
          query: GRAPHQL_QUERY,
          variables: params
        })
      });

      const json = await response.json();
      if (json?.errors?.length) {
        const first = json.errors[0];
        return Promise.reject({
          message: first.message || 'Export request failed',
          statusCode: String(response.status)
        });
      }
      return json?.data?.exportReadingList as TYPES.MutationResponse;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const exportReadingListSlice = createSlice({
  name: 'export_reading_list',
  initialState: initialState,
  reducers: {
    exportReadingListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(exportReadingListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(exportReadingListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(exportReadingListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { exportReadingListClear } = exportReadingListSlice.actions;

export const exportReadingList = createSelector(
  (state: RootState) => state.reading.exportReadingList.result,
  (state: RootState) => state.reading.exportReadingList.error,
  (state: RootState) => state.reading.exportReadingList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default exportReadingListSlice.reducer;
